// The plan ladder and AI-credit economics the admin console works to.
//
// Source: projects/GROWTH-IMPLEMENTATION.md, sections 1 (Plans) and 2 (Credits),
// decided by the owner on 2026-10-16. This file is the admin console's copy of
// that contract (the spec lists "haptyx-shared/src/plans.ts and its copies").
// tests/ladder.spec.ts pins every number here to the spec table, so a change to
// the ladder has to be made on purpose in both places.
//
// Why a local copy rather than `@shared/plans`: the shared module is being moved
// to the new ladder by another workstream, and its TierId union only knows
// free / pro / team. The console must be able to show, filter and set Pro+ and
// Enterprise whether or not the shared module has caught up, and must not stop
// compiling the day it does. What the SERVER actually serves is read live from
// GET /public/plans and shown beside these numbers (see Subscriptions), so any
// drift between the contract and production is visible rather than hidden.

export type TierId = 'free' | 'pro' | 'proplus' | 'team' | 'enterprise'

/** Every tier in ladder order. Use this for filters, pickers and matrix columns. */
export const TIER_IDS: readonly TierId[] = ['free', 'pro', 'proplus', 'team', 'enterprise']

/** Models available on managed AI, by short family name. */
export type ModelFamily = 'haiku' | 'sonnet' | 'opus'

export interface TierSpec {
  id: TierId
  name: string
  /** Monthly list price in USD. null = custom (Enterprise, contact sales). */
  priceMonthly: number | null
  /** Yearly list price in USD (two months free). null = custom. */
  priceYearly: number | null
  /** What one unit of the price buys. */
  unit: 'user' | 'seat' | 'custom'
  /** Monthly managed-AI allowance in credits. null = custom. Per seat for Team. */
  allowanceCredits: number | null
  /** Team's allowance is pooled per org rather than held per person. */
  allowancePooled: boolean
  /** Models the tier may call on managed AI. */
  models: ModelFamily[] | 'all'
  /** max_tokens cap the proxy applies. null = not fixed by the spec. */
  maxTokens: number | null
  /** Short description for the admin plan cards. */
  summary: string
}

export const TIER_LADDER: readonly TierSpec[] = [
  {
    id: 'free',
    name: 'Free',
    priceMonthly: 0,
    priceYearly: 0,
    unit: 'user',
    allowanceCredits: 100,
    allowancePooled: false,
    models: ['haiku'],
    maxTokens: 2048,
    summary: 'Every app, bring your own key, 100 managed credits a month on Haiku.'
  },
  {
    id: 'pro',
    name: 'Pro',
    priceMonthly: 12,
    priceYearly: 120,
    unit: 'user',
    allowanceCredits: 800,
    allowancePooled: false,
    models: ['haiku', 'sonnet'],
    maxTokens: 8192,
    summary: 'Managed AI with 800 credits a month on Haiku and Sonnet.'
  },
  {
    id: 'proplus',
    name: 'Pro+',
    priceMonthly: 25,
    priceYearly: 250,
    unit: 'user',
    allowanceCredits: 2500,
    allowancePooled: false,
    models: ['haiku', 'sonnet', 'opus'],
    maxTokens: 16384,
    summary: 'Everything in Pro with 2,500 credits a month and Opus.'
  },
  {
    id: 'team',
    name: 'Team',
    priceMonthly: 15,
    priceYearly: 150,
    unit: 'seat',
    allowanceCredits: 800,
    allowancePooled: true,
    models: ['haiku', 'sonnet'],
    maxTokens: 8192,
    summary: 'Per seat (owner, admin, member; guests free). 800 credits per seat, pooled per org.'
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    priceMonthly: null,
    priceYearly: null,
    unit: 'custom',
    allowanceCredits: null,
    allowancePooled: true,
    models: 'all',
    maxTokens: null,
    summary: 'Custom price and allowance through sales. Every model.'
  }
]

export function isTierId(x: unknown): x is TierId {
  return typeof x === 'string' && (TIER_IDS as readonly string[]).includes(x)
}

export function tierSpec(id: string): TierSpec | undefined {
  return TIER_LADDER.find((t) => t.id === id)
}

/** Display name for a tier id. An id the console does not know is shown as-is
 *  rather than mapped to a guess, so a new server tier is visible, not hidden. */
export function tierName(id: string | null | undefined): string {
  if (!id) return '—'
  return tierSpec(id)?.name ?? id
}

/** Badge colours per tier, shared by every page that shows a tier. */
export function tierTone(id: string): string {
  switch (id) {
    case 'enterprise':
      return 'bg-amber-500/15 text-amber-300'
    case 'team':
      return 'bg-accent/20 text-accent'
    case 'proplus':
      return 'bg-fuchsia-500/15 text-fuchsia-300'
    case 'pro':
      return 'bg-indigo-500/15 text-indigo-300'
    case 'free':
      return 'bg-white/5 text-slate-300'
    default:
      // An unknown tier gets a warning tone so it stands out.
      return 'bg-red-500/15 text-red-300'
  }
}

// ── Credits ───────────────────────────────────────────────────────────────
// 1 credit = US$0.01 of billed usage. Billed = Anthropic list cost x markup.

export const CREDIT_USD = 0.01
export const DEFAULT_CREDIT_MARKUP = 2.0

/** Bonus credits granted on email verification (the trial grant). */
export const TRIAL_BONUS_CREDITS = 300
/** Referral AI-credit rewards (section 4): both sides on verification,
 *  referrer again on first payment. */
export const REFERRAL_VERIFY_CREDITS = 500
export const REFERRAL_PAID_CREDITS = 1000
export const REFERRAL_CAP_PER_YEAR = 20

/** Referral credit bank (section 4b): US$12 per qualifying referral, at most
 *  US$144 earned per rolling 12 months. Dollars toward subscriptions only;
 *  never AI credits, never cash. */
export const REFERRAL_BANK_USD = 12
export const REFERRAL_BANK_CAP_USD = 144

export type CreditBucketKind = 'allowance' | 'bonus' | 'purchased'

/** Spend order and expiry rules for the three buckets. */
export const CREDIT_BUCKETS: readonly { kind: CreditBucketKind; label: string; rule: string }[] = [
  { kind: 'allowance', label: 'Monthly allowance', rule: 'Resets on the billing anniversary (1st of the month UTC on Free). Never rolls over.' },
  { kind: 'bonus', label: 'Bonus', rule: 'Trial and referral grants. Expire 90 days after grant.' },
  { kind: 'purchased', label: 'Purchased', rule: 'Top-ups. Expire 12 months after purchase.' }
]

export interface TopUpPack {
  usd: number
  credits: number
}

/** One-off Stripe top-ups. Minimum $10. */
export const TOPUP_PACKS: readonly TopUpPack[] = [
  { usd: 10, credits: 1000 },
  { usd: 25, credits: 2600 },
  { usd: 50, credits: 5500 }
]

/** Billed USD to credits. Exact by definition (1 credit = $0.01 billed); rounded
 *  to the nearest credit to drop binary float noise. */
export function usdToCredits(usd: number): number {
  return Math.round(usd / CREDIT_USD)
}

export function creditsToUsd(credits: number): number {
  return Math.round(credits) * CREDIT_USD
}
